import { FormController } from "$root/third-party-integrations/react-hook-form";
import { valueByPath } from "$root/utils";
import type { StylableProps } from "@mdotm/shadcn/legacy/components";
import { type SwitchProps, Switch, FormField } from "@mdotm/shadcn/mdotm/components";
import type { ReactNode } from "react";
import type { Control, FieldValues, FormState, Path } from "react-hook-form";

/**
 * The switch half of what used to be one field.
 *
 * `FormFields.Checkbox` forwarded the legacy `switchType` prop straight through to the legacy
 * `Checkbox`, so three of its call sites were rendering toggle switches rather than tick boxes
 * (`spawn-customer-dialog.tsx`, all three in a collapsible header). MDOT-24853 split that dual-mode
 * component in two, which leaves no prop to forward — so those sites need a field of their own or
 * they silently become tick boxes.
 *
 * Deliberately a copy of `CheckboxField` rather than a shared generic parameterised by control: the
 * two differ only in which component they render, and a wrapper discriminated on a `switchType`-ish
 * prop would rebuild the exact dual-mode component this epic exists to delete.
 *
 * `Switch` takes a boolean-only `checked`, where `Checkbox` also accepts `'indeterminate'` — which
 * is the substantive difference between the two fields. Note the type does NOT enforce it here:
 * `checked` is `Omit`-ed out of the public props and supplied from react-hook-form's untyped
 * `field.value`, so a form storing `'indeterminate'` under this field would reach the switch
 * unchallenged. `Switch` defaults an `undefined` value to `false` for the same reason.
 */
export type SwitchFieldProps<T extends FieldValues> = StylableProps & {
	control: Control<T, any>;
	formState: FormState<T>;
	name: Path<T>;
	label?: ReactNode;
	children?: ReactNode;
} & Omit<SwitchProps, "checked">;

export function SwitchField<T extends FieldValues>({
	label,
	name,
	formState,
	control,
	className,
	style,
	children,
	...forward
}: SwitchFieldProps<T>): React.JSX.Element {
	return (
		<FormField
			className={className}
			style={style}
			label={label}
			error={(valueByPath(formState.errors, name) as { message?: string })?.message}
		>
			{(fieldProps) => (
				<FormController
					control={control}
					name={name}
					render={({ field: { ref, value, onChange, ...controllerProps } }) => (
						<Switch
							innerRef={ref}
							id={fieldProps.id}
							invalid={fieldProps.invalid}
							{...controllerProps}
							{...forward}
							onChange={(x) => {
								onChange(x);
								forward.onChange?.(x);
							}}
							checked={value}
						>
							{children}
						</Switch>
					)}
				/>
			)}
		</FormField>
	);
}
